import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useState, useMemo, useRef, PropsWithChildren } from 'react'
import {
  DataLayerObject,
  resetAndPushNewPageView,
  compareAndPushEventInDataLayer,
  pushToDataLayer,
  MemberID,
  toMemberId,
} from '~/utils/googleTagManager'
import { isProduction } from '~/utils/envChecks'
import { getKameleoonVisitorCode } from '~/utils/kameleoon/kameleoon'
import { getGAEventProgrammeCodeForOpCo } from '~/utils/googleTagManager/googleTagManagerHelperFunctions'
import { OpCoClassName } from '@reward-platform/platform-config'
import { TagManagerContext, PushEventToDataLayer } from './TagManagerContext'
import { useUser } from '../user'
import { useChannel } from './useChannel'
import { usePlatformConfig } from '../platform'
import { useAuthContext } from '../auth'
import { useAccount } from '../account'

type Event = {
  eventName: string
  data?: DataLayerObject
}

export type GlobalEvent = {
  isLoggedIn: boolean
  Iso?: string
  product: 'reward_platform'
  programmeCode?: OpCoClassName
  memberID?: MemberID | null
  kameleoon_visitor_code?: string | null
  balance?: number | null
  domain_environment: 'prod' | 'non-prod'
  channel?: string
}

export const TagManagerProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter()
  const [isPageViewEventSent, setIsPageViewEventSent] = useState(false)
  const isPageViewEventSentRef = useRef(false)
  const isInitialGlobalEventSentRef = useRef(false)
  const [bufferedEvents, setBufferedEvents] = useState<Event[]>([])
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthContext()
  const { user, isLoading: isUserLoading } = useUser()
  const { aviosBalance, isLoading: isAccountLoading } = useAccount()
  const userId = user?.memberIdentifier?.identifier

  const iso = user?.person.countryOfResidence.identifier
  const channel = useChannel()
  const { opCo } = usePlatformConfig()

  // If the page view event has not been sent, add the event to the buffer
  const pushEventToDataLayer = useCallback<PushEventToDataLayer>(
    (eventName, data) => {
      if (isPageViewEventSent) {
        compareAndPushEventInDataLayer(eventName, data)
      } else {
        setBufferedEvents((oldEventBuffer) => {
          return [...oldEventBuffer, { eventName, data }]
        })
      }
    },
    [isPageViewEventSent]
  )

  // If the page view  event has not been sent, add the event to the buffer
  const onRouteChangeComplete = useCallback(() => {
    if (isInitialGlobalEventSentRef.current && !isPageViewEventSentRef.current && channel) {
      resetAndPushNewPageView({ channel })
      isPageViewEventSentRef.current = true
      setIsPageViewEventSent(true)
    }
  }, [channel])

  useEffect(() => {
    if (
      !isAuthLoading &&
      !isUserLoading &&
      !isAccountLoading &&
      !isInitialGlobalEventSentRef.current &&
      channel
    ) {
      const globalEvent: GlobalEvent = {
        isLoggedIn: isAuthenticated,
        Iso: iso,
        product: 'reward_platform',
        programmeCode: getGAEventProgrammeCodeForOpCo(opCo),
        memberID: toMemberId(userId),
        kameleoon_visitor_code: getKameleoonVisitorCode(),
        balance: aviosBalance,
        domain_environment: isProduction() ? 'prod' : 'non-prod',
        channel,
      }
      if (!isAuthenticated || !userId) {
        globalEvent.isLoggedIn = false
        globalEvent.Iso = 'GB'
        globalEvent.memberID = null
        globalEvent.balance = null
      }
      pushToDataLayer(globalEvent)
      isInitialGlobalEventSentRef.current = true
      onRouteChangeComplete()
    }
  }, [
    isUserLoading,
    isAccountLoading,
    user,
    isAuthenticated,
    iso,
    opCo,
    userId,
    aviosBalance,
    channel,
    onRouteChangeComplete,
    isAuthLoading,
  ])

  const pushBufferedEvents = useCallback(() => {
    setBufferedEvents((currentBuffer) => {
      if (currentBuffer.length > 0) {
        currentBuffer.forEach(({ eventName, data }) =>
          compareAndPushEventInDataLayer(eventName, data)
        )
      }
      return []
    })
  }, [])

  // Once the page view event has been sent, send the buffered events
  useEffect(() => {
    if (bufferedEvents.length > 0 && isPageViewEventSent) {
      pushBufferedEvents()
    }
  }, [bufferedEvents.length, isPageViewEventSent, pushBufferedEvents])

  useEffect(() => {
    const onRouteChangeStart = () => {
      setBufferedEvents([])
      isPageViewEventSentRef.current = false
      setIsPageViewEventSent(false)
    }

    router.events.on('routeChangeStart', onRouteChangeStart)
    router.events.on('routeChangeComplete', onRouteChangeComplete)
    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
      router.events.off('routeChangeComplete', onRouteChangeComplete)
    }
  }, [onRouteChangeComplete, router.events])

  const context = useMemo(() => ({ pushEventToDataLayer }), [pushEventToDataLayer])

  return <TagManagerContext.Provider value={context}>{children}</TagManagerContext.Provider>
}
